import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, FileText, Clock, CheckCircle, Award, Download, Printer, Brain, Sparkles, PlayCircle, Volume2, X, ChevronLeft as PrevIcon, ChevronRight as NextIcon, ChevronRight, ChevronLeft, User, ExternalLink } from "lucide-react";
import confetti from "canvas-confetti";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ReactPlayer from "react-player";
import LoadingQuip from "@/components/LoadingQuip";
import { getDisplayName } from "@/utils/displayName";
import DarkModeToggle from "@/components/DarkModeToggle";
import { useTheme } from "@/contexts/ThemeContext";
import { LessonPdfViewer } from "@/components/LessonPdfViewer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";

function CoursePage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  
  // Theme-aware colors for dark mode support
  const colors = {
    pageBg: isDarkMode ? '#092036' : '#F4F4F4',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    headerBg: isDarkMode ? '#092036' : 'white',
    sidebarBg: isDarkMode ? '#0f2744' : 'white',
    heading: isDarkMode ? '#ffffff' : '#102862',
    text: isDarkMode ? '#e2e8f0' : '#092036',
    subtext: isDarkMode ? '#94a3b8' : '#70767C',
    mutedText: isDarkMode ? '#64748b' : '#6b7280',
    border: isDarkMode ? '#1e3a5f' : '#e5e7eb',
    inputBg: isDarkMode ? '#1e3a5f' : 'white',
    inputText: isDarkMode ? '#ffffff' : '#092036',
    hoverBg: isDarkMode ? '#1e3a5f' : '#f3f4f6',
    modalBg: isDarkMode ? '#0f2744' : 'white',
    accent: isDarkMode ? '#60a5fa' : '#102862',
  };
  
  // Core state
  const [course, setCourse] = useState(null);
  const [sections, setSections] = useState([]);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  
  // Progress and tracking
  const [progress, setProgress] = useState(null);
  const [completedLessons, setCompletedLessons] = useState(new Set());
  const [currentLessonIndex, setCurrentLessonIndex] = useState(0);
  const sessionStartTimeRef = useRef(null);
  const [totalTimeSpent, setTotalTimeSpent] = useState(0);
  
  // Assessment state
  const [assessmentData, setAssessmentData] = useState(null);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [submittingAssessment, setSubmittingAssessment] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [assessmentAnswers, setAssessmentAnswers] = useState({});
  
  // Survey state
  const [survey, setSurvey] = useState(null);
  const [surveyAnswers, setSurveyAnswers] = useState([]);
  const [showSurvey, setShowSurvey] = useState(false);
  const [submittingSurvey, setSubmittingSurvey] = useState(false);
  
  // Certificate state
  const [showCertificate, setShowCertificate] = useState(false);
  const [certificateUrl, setCertificateUrl] = useState(null);
  
  // UI state
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showLessonView, setShowLessonView] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showFloatingChat, setShowFloatingChat] = useState(false);
  const [externalLinkUrl, setExternalLinkUrl] = useState(null);
  const [showExternalLinkModal, setShowExternalLinkModal] = useState(false);
  
  // Function to open external links in a popup window
  const openExternalLink = (url) => {
    // Calculate popup dimensions (80% of screen)
    const width = Math.min(1400, window.innerWidth * 0.85);
    const height = Math.min(900, window.innerHeight * 0.85);
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;
    
    // Open popup window
    const popup = window.open(
      url,
      'ExternalContent',
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes,toolbar=no,menubar=no,location=yes`
    );
    
    // Focus the popup if it opened successfully
    if (popup) {
      popup.focus();
    } else {
      // If popup was blocked, fall back to new tab
      window.open(url, '_blank');
    }
  };
  
  // Video state
  const [videoPosition, setVideoPosition] = useState(0);
  const [seekToPosition, setSeekToPosition] = useState(null);
  const videoPlayerRef = useRef(null);
  const lastSavedPositionRef = useRef(0);
  
  // AI Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [suggestionMessages, setSuggestionMessages] = useState([]);
  
  // Refs
  const chatMessagesRef = useRef(null);
  const lessonContentRef = useRef(null);
  const suggestionIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isActiveRef = useRef(true);
  
  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch course data
  useEffect(() => {
    if (courseId) {
      fetchCourse();
      fetchSources();
      fetchProgress();
      sessionStartTimeRef.current = Date.now();
    }
    
    return () => {
      if (sessionStartTimeRef.current) {
        saveTimeSpent();
      }
    };
  }, [courseId]);

  // Track user activity
  useEffect(() => {
    const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
    
    const updateActivity = () => {
      lastActivityRef.current = Date.now();
      if (!isActiveRef.current) {
        // User became active again, restart session timer
        isActiveRef.current = true;
        sessionStartTimeRef.current = Date.now();
      }
    };
    
    // Activity detection events
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      document.addEventListener(event, updateActivity, { passive: true });
    });
    
    // Check for inactivity every 30 seconds
    const inactivityCheck = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      
      if (timeSinceLastActivity > INACTIVITY_THRESHOLD && isActiveRef.current) {
        // User is inactive, save time up to last activity and pause
        isActiveRef.current = false;
        saveTimeSpent();
        sessionStartTimeRef.current = null; // Stop tracking
      }
    }, 30000);
    
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      clearInterval(inactivityCheck);
    };
  }, []);

  // Auto-save time every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      saveTimeSpent();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Separate actual lessons from external resources (URL types) - defined early for use in useEffect
  const actualLessons = useMemo(() => sources.filter(s => s.source_type !== 'url'), [sources]);
  
  // Auto-open first incomplete lesson OR show new layout for empty courses
  useEffect(() => {
    if (!sourcesLoading && !showLessonView) {
      if (actualLessons.length > 0) {
        // Find first incomplete lesson (only actual lessons, not external resources)
        const firstIncompleteIndex = actualLessons.findIndex(lesson => !completedLessons.has(lesson.id));
        const lessonIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex : 0;
        
        // Auto-open the lesson
        openLesson(actualLessons[lessonIndex], lessonIndex);
      } else {
        // No lessons - still show new layout with empty state
        setShowLessonView(true);
      }
    }
  }, [sourcesLoading, actualLessons, completedLessons, showLessonView]);

  // Scroll chat to bottom
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages]);

  const fetchCourse = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}`, { headers });
      setCourse(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Error loading course");
      setLoading(false);
    }
  };

  const fetchSources = async () => {
    const cacheKey = `course_sources_${courseId}`;
    
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const cachedData = JSON.parse(cached);
        // Handle flat array structure
        setSources(Array.isArray(cachedData) ? cachedData : []);
        setSourcesLoading(false);
      }
    } catch (error) {
      console.error("Error loading cached sources:", error);
    }
    
    try {
      const response = await axios.get(`${API}/courses/${courseId}/sources`, { headers });
      const data = response.data;
      
      localStorage.setItem(cacheKey, JSON.stringify(data));
      
      // Data is now a simple flat array of lessons
      setSources(Array.isArray(data) ? data : []);
      setSourcesLoading(false);
    } catch (error) {
      toast.error("Error loading sources");
      setSourcesLoading(false);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/progress`, { headers });
      setProgress(response.data);
      setCompletedLessons(new Set(response.data.completed_sources || []));
      setTotalTimeSpent(response.data.time_spent_minutes || 0);
    } catch (error) {
      console.error("Error loading progress:", error);
    }
  };

  const saveTimeSpent = async () => {
    // Only save if session is active and timer is running
    if (!sessionStartTimeRef.current || !isActiveRef.current) return;
    
    const sessionMinutes = Math.floor((Date.now() - sessionStartTimeRef.current) / 60000);
    
    // Only save if at least 1 minute has passed
    if (sessionMinutes < 1) return;
    
    const newTotalTime = totalTimeSpent + sessionMinutes;
    
    try {
      await axios.post(
        `${API}/progress/${courseId}/time`,
        { minutes: sessionMinutes },
        { headers }
      );
      setTotalTimeSpent(newTotalTime);
      sessionStartTimeRef.current = Date.now();
    } catch (error) {
      console.error("Error saving time");
    }
  };

  const markLessonComplete = async (lessonId) => {
    if (completedLessons.has(lessonId)) return;
    
    try {
      await axios.post(
        `${API}/progress/${courseId}/complete`,
        { source_id: lessonId },
        { headers }
      );
      
      const newCompleted = new Set(completedLessons);
      newCompleted.add(lessonId);
      setCompletedLessons(newCompleted);
      
      toast.success("Lesson marked as complete!");
      await fetchProgress();
      
      // Auto-advance to next lesson if available
      if (currentLessonIndex < sources.length - 1) {
        setTimeout(() => {
          goToNextLesson();
        }, 500);
      }
    } catch (error) {
      toast.error("Error marking lesson complete");
    }
  };

  const saveVideoPosition = async (lessonId, positionSeconds) => {
    // Only save if position has changed by at least 5 seconds
    if (Math.abs(positionSeconds - lastSavedPositionRef.current) < 5) {
      return;
    }
    
    try {
      await axios.post(
        `${API}/progress/${courseId}/video-position`,
        { lesson_id: lessonId, position_seconds: positionSeconds },
        { headers }
      );
      lastSavedPositionRef.current = positionSeconds;
    } catch (error) {
      console.error("Error saving video position");
    }
  };

  const handleVideoProgress = (progress) => {
    if (selectedLesson && selectedLesson.video_url) {
      const currentPosition = progress.playedSeconds;
      setVideoPosition(currentPosition);
      
      // Save position every 10 seconds of playback
      saveVideoPosition(selectedLesson.id, currentPosition);
    }
  };

  const normalizeVimeoUrl = (url) => {
    if (!url || !url.includes('vimeo')) return url;
    
    // Extract video ID from various Vimeo URL formats
    let videoId = null;
    
    // Format: https://vimeo.com/1140754948
    if (url.match(/vimeo\.com\/(\d+)/)) {
      videoId = url.match(/vimeo\.com\/(\d+)/)[1];
    }
    // Format: https://player.vimeo.com/video/1140754948
    else if (url.match(/player\.vimeo\.com\/video\/(\d+)/)) {
      videoId = url.match(/player\.vimeo\.com\/video\/(\d+)/)[1];
    }
    
    // Return the player.vimeo.com format which works best with ReactPlayer
    if (videoId) {
      return `https://player.vimeo.com/video/${videoId}`;
    }
    
    return url;
  };

  const getVideoUrlWithStartTime = (url, startSeconds) => {
    if (!url || !startSeconds || startSeconds < 5) return url;
    
    // For YouTube videos, append start time parameter
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const separator = url.includes('?') ? '&' : '?';
      return `${url}${separator}t=${Math.floor(startSeconds)}s`;
    }
    
    // For Vimeo videos, append start time parameter (#t=XXXs format)
    if (url.includes('vimeo.com')) {
      // Remove any existing fragment (#) first
      const baseUrl = url.split('#')[0];
      return `${baseUrl}#t=${Math.floor(startSeconds)}s`;
    }
    
    return url;
  };

  const handleVideoReady = () => {
    // Seek is handled via URL parameter for YouTube, so only needed for non-YouTube videos
    if (seekToPosition && seekToPosition > 5 && videoPlayerRef.current) {
      const isYouTube = selectedLesson?.video_url?.includes('youtube.com') || selectedLesson?.video_url?.includes('youtu.be');
      if (!isYouTube) {
        setTimeout(() => {
          videoPlayerRef.current.seekTo(seekToPosition, 'seconds');
          setSeekToPosition(null);
        }, 100);
      }
    }
  };


  const openLesson = async (lesson, index) => {
    setSelectedLesson(lesson);
    setCurrentLessonIndex(index);
    setShowLessonView(true);
    setSeekToPosition(0);
    
    // Scroll to top when opening lesson - both window and lesson content container
    window.scrollTo({ top: 0, behavior: 'instant' });
    setTimeout(() => {
      if (lessonContentRef.current) {
        lessonContentRef.current.scrollTo({ top: 0, behavior: 'instant' });
      }
    }, 50);
    
    // Load saved video position if lesson has video
    if (lesson.video_url) {
      try {
        const response = await axios.get(
          `${API}/progress/${courseId}/video-position/${lesson.id}`,
          { headers }
        );
        const savedPosition = response.data.position_seconds || 0;
        // Only set if position is greater than 5 seconds
        if (savedPosition > 5) {
          setSeekToPosition(savedPosition);
        }
      } catch (error) {
        console.error("Error loading video position");
      }
    }
  };

  const closeLesson = () => {
    // Return to dashboard instead of course overview
    navigate('/dashboard');
  };

  const goToPreviousLesson = () => {
    if (currentLessonIndex > 0) {
      const newIndex = currentLessonIndex - 1;
      setCurrentLessonIndex(newIndex);
      setSelectedLesson(allLessons[newIndex]);
      
      // Scroll to top - both window and lesson content container
      window.scrollTo({ top: 0, behavior: 'instant' });
      setTimeout(() => {
        if (lessonContentRef.current) {
          lessonContentRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
      }, 50);
    }
  };

  const goToNextLesson = () => {
    if (currentLessonIndex < allLessons.length - 1) {
      const newIndex = currentLessonIndex + 1;
      setCurrentLessonIndex(newIndex);
      setSelectedLesson(allLessons[newIndex]);
      
      // Scroll to top - both window and lesson content container
      window.scrollTo({ top: 0, behavior: 'instant' });
      setTimeout(() => {
        if (lessonContentRef.current) {
          lessonContentRef.current.scrollTo({ top: 0, behavior: 'instant' });
        }
      }, 50);
    }
  };

  // Assessment functions
  const loadAssessment = async () => {
    setLoadingAssessment(true);
    try {
      const response = await axios.get(`${API}/courses/${courseId}/assessment`, { headers });
      console.log("Assessment loaded:", response.data);
      setAssessmentData(response.data);
      setShowAssessment(true);
      console.log("showAssessment set to TRUE");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error loading assessment";
      toast.error(errorMsg);
      console.error("Assessment load error:", error);
    }
    setLoadingAssessment(false);
  };

  const submitAssessment = async () => {
    setSubmittingAssessment(true);
    try {
      // Convert assessmentAnswers object to array format backend expects
      const answersArray = Object.entries(assessmentAnswers).map(([questionIdx, selectedOption]) => {
        const question = assessmentData.questions[parseInt(questionIdx)];
        const answerIndex = question.options.findIndex(opt => opt === selectedOption);
        
        return {
          question_idx: parseInt(questionIdx),
          answer: answerIndex,
          question: question
        };
      });
      
      const response = await axios.post(
        `${API}/courses/${courseId}/assessment`,
        { answers: answersArray },
        { headers }
      );
      
      const { score, passed } = response.data;
      
      console.log('Assessment result:', { score, passed, passingGrade: course.passing_grade });
      
      if (passed) {
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        toast.success(`Congratulations! You scored ${score}% and passed the course!`, {
          description: 'Complete the course survey to receive your certificate.'
        });
        
        console.log('Student passed! Preparing to redirect to survey...');
        
        // Close modal and redirect to survey
        setShowAssessment(false);
        setAssessmentAnswers({});
        await fetchProgress();
        
        console.log('About to schedule redirect in 2 seconds...');
        
        // Redirect to universal course survey (required for CE credit)
        setTimeout(() => {
          console.log('Executing redirect NOW to:', `/course-survey?courseId=${courseId}&courseTitle=${encodeURIComponent(course.title)}`);
          navigate(`/course-survey?courseId=${courseId}&courseTitle=${encodeURIComponent(course.title)}`);
        }, 2000);
      } else {
        toast.error(`You scored ${score}%. You need ${course.passing_grade}% to pass.`);
        await fetchProgress();
        setShowAssessment(false);
        setAssessmentAnswers({});
      }
    } catch (error) {
      console.error("Assessment submission error:", error);
      toast.error(`Error submitting assessment: ${error.response?.data?.detail || error.message}`);
    }
    setSubmittingAssessment(false);
  };

  const loadSurvey = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/survey`, { headers });
      setSurvey(response.data);
      setSurveyAnswers(new Array(response.data.questions.length).fill(""));
      setShowSurvey(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error loading survey");
    }
  };

  const submitSurvey = async () => {
    setSubmittingSurvey(true);
    try {
      await axios.post(
        `${API}/courses/${courseId}/survey`,
        { answers: surveyAnswers },
        { headers }
      );
      
      toast.success("Survey submitted! Your certificate is now available.");
      setShowSurvey(false);
      await fetchProgress();
      
      // Generate and show certificate in modal
      setTimeout(() => openCertificate(), 1000);
    } catch (error) {
      toast.error("Error submitting survey");
    }
    setSubmittingSurvey(false);
  };

  const openCertificate = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/certificate/download`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setCertificateUrl(url);
      setShowCertificate(true);
    } catch (error) {
      console.error("Certificate loading error:", error);
      toast.error(`Error loading certificate: ${error.response?.data?.detail || error.message}`);
    }
  };

  const downloadCertificate = async () => {
    try {
      const response = await axios.get(`${API}/courses/${courseId}/certificate/download`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${course.title}_Certificate.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Certificate downloaded!');
    } catch (error) {
      console.error("Certificate download error:", error);
      toast.error('Failed to download certificate. Please try again.');
    }
  };

  // AI Chat functions
  const sendMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = { role: "user", content: chatInput };
    setChatMessages([...chatMessages, userMessage]);
    setChatInput("");
    setChatLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/chat/${courseId}`,
        { 
          message: chatInput,
          conversation_history: chatMessages.slice(-5)
        },
        { headers }
      );
      
      const aiMessage = { role: "assistant", content: response.data.response };
      setChatMessages(prev => [...prev, aiMessage]);
      
      // Refresh progress to update questions_asked counter
      await fetchProgress();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error sending message. Please try again.";
      toast.error(errorMsg, {
        duration: 5000,
        description: error.response?.status === 503 ? "The AI Assistant requires an active OpenAI API key." : ""
      });
      setChatMessages(prev => prev.slice(0, -1));
      console.error("Chat error:", error);
    }
    
    setChatLoading(false);
  };

  // Use actualLessons (defined earlier) for allLessons reference and external resources
  const allLessons = actualLessons;
  const externalResources = useMemo(() => sources.filter(s => s.source_type === 'url'), [sources]);
  
  const completionPercentage = allLessons.length > 0 
    ? Math.round((completedLessons.size / allLessons.length) * 100) 
    : 0;
  
  // Check if assessment requirements are met
  const questionsAsked = progress?.questions_asked || 0;
  const questionsRequirementMet = questionsAsked >= (course?.required_questions || 0);
  const timeRequirementMet = totalTimeSpent >= (course?.min_time_minutes || 0);
  // Assessment always available - no requirements
  const assessmentAvailable = !progress?.assessment_passed;

  if (loading || sourcesLoading) {
    return <LoadingQuip />;
  }

  if (!course) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Course not found</p>
      </div>
    );
  }

  // Render lesson view (new layout) - always use this layout
  if (showLessonView) {
    return (
      <div className="h-screen flex flex-col overflow-hidden w-full max-w-full" style={{ backgroundColor: colors.pageBg }}>
        {/* Top Navigation Bar - Fixed */}
        <div className="shadow-sm z-30 flex-shrink-0" style={{ backgroundColor: colors.headerBg, borderBottom: `1px solid ${colors.border}` }}>
          <div className="max-w-screen-2xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={closeLesson}
                  className="p-2 rounded-full transition-colors flex items-center gap-2"
                  style={{ color: colors.heading }}
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">Back to Course</span>
                </button>
                <div className="h-6 w-px" style={{ backgroundColor: colors.border }} />
                <h1 className="text-lg font-bold" style={{ color: colors.heading }}>
                  {course.title}
                </h1>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-sm" style={{ color: colors.subtext }}>
                  {allLessons.length > 0 ? `Lesson ${currentLessonIndex + 1} of ${allLessons.length}` : 'No lessons yet'}
                </div>
                <DarkModeToggle />
              </div>
            </div>
          </div>
        </div>

        {/* Progress Indicators - Fixed */}
        <div className="flex-shrink-0" style={{ backgroundColor: colors.headerBg, borderBottom: `1px solid ${colors.border}` }}>
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 max-w-2xl mx-auto">
              {/* Progress Card with Green Gradient */}
              <Card className="p-3 md:p-4" style={{ backgroundColor: colors.cardBg }}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" style={{ color: colors.accent }} />
                    <span className="text-sm font-medium" style={{ color: colors.text }}>Course Progress</span>
                  </div>
                  <span className="text-xl md:text-2xl font-bold" style={{ color: completionPercentage > 50 ? '#059669' : '#10b981' }}>
                    {Math.round(completionPercentage)}%
                  </span>
                </div>
                <div className="relative h-2 md:h-3 rounded-full overflow-hidden" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}>
                  <div 
                    className="absolute top-0 left-0 h-full transition-all duration-500 rounded-full"
                    style={{ 
                      width: `${completionPercentage}%`,
                      background: `linear-gradient(to right, #86efac, ${completionPercentage > 50 ? '#059669' : '#10b981'})`
                    }}
                  />
                </div>
                <p className="text-xs mt-2" style={{ color: colors.subtext }}>
                  {completedLessons.size} / {allLessons.length} lessons completed
                </p>
              </Card>

              {/* Assessment Card */}
              <Card className={`p-3 md:p-4 transition-colors`} style={{ backgroundColor: isDarkMode ? (progress?.assessment_passed ? '#1e3a8a' : '#14532d') : (progress?.assessment_passed ? '#eff6ff' : '#f0fdf4'), borderColor: isDarkMode ? (progress?.assessment_passed ? '#3b82f6' : '#22c55e') : (progress?.assessment_passed ? '#93c5fd' : '#86efac') }}>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className={`w-4 h-4 ${progress?.assessment_passed ? 'text-blue-500' : 'text-green-500'}`} />
                  <span className="text-sm font-medium" style={{ color: colors.text }}>Assessment</span>
                </div>
                {progress?.assessment_passed ? (
                  <div className="flex items-center gap-2 py-3">
                    <Award className="w-6 h-6 text-blue-500" />
                    <span className="text-base font-semibold text-blue-500">Passed!</span>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    onClick={loadAssessment}
                    disabled={loadingAssessment}
                    className="w-full bg-green-600 hover:bg-green-700 mt-2"
                  >
                    {loadingAssessment ? "Loading..." : "Take Assessment"}
                  </Button>
                )}
              </Card>
            </div>
          </div>
        </div>

        <div className="max-w-screen-2xl mx-auto flex flex-1 overflow-hidden w-full">
          {/* LEFT: Collapsible Lesson Sidebar - Scrollable (Hidden on Mobile) */}
          <div className={`${sidebarCollapsed ? 'w-16' : 'w-80'} transition-all duration-300 flex-shrink-0 overflow-y-auto hidden lg:block`} style={{ backgroundColor: colors.sidebarBg, borderRight: `1px solid ${colors.border}` }}>
            <div className="p-4">
              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className="w-full flex items-center justify-center p-2 rounded mb-4"
                style={{ color: colors.text }}
              >
                {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
              </button>
              
              {!sidebarCollapsed && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold mb-3" style={{ color: colors.heading }}>Course Lessons</h3>
                  {allLessons.length > 0 ? (
                    allLessons.map((lesson, index) => {
                      const isCompleted = completedLessons.has(lesson.id);
                      const isCurrent = selectedLesson && lesson.id === selectedLesson.id;
                      return (
                        <button
                          key={lesson.id}
                          onClick={() => openLesson(lesson, index)}
                          className={`w-full text-left p-3 rounded-lg transition-all border-2`}
                          style={{ 
                            backgroundColor: isCurrent ? (isDarkMode ? '#1e3a5f' : '#eff6ff') : 'transparent',
                            borderColor: isCurrent ? (isDarkMode ? '#3b82f6' : '#93c5fd') : 'transparent'
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0">
                              {isCompleted ? (
                                <CheckCircle className="w-5 h-5 text-green-500" />
                              ) : (
                                <div className="w-5 h-5 rounded-full border-2" style={{ borderColor: colors.subtext }} />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate" style={{ color: isCurrent ? colors.accent : colors.text }}>
                                {index + 1}. {lesson.title}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })
                  ) : (
                    <p className="text-sm text-center py-4" style={{ color: colors.subtext }}>No lessons yet</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* CENTER: Lesson Content - Scrollable */}
          <div ref={lessonContentRef} className="flex-1 p-4 md:p-8 max-w-4xl overflow-y-auto w-full">
            {selectedLesson ? (
              <>
                {/* Lesson Title */}
                <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-center" style={{ color: colors.heading }}>
                  {selectedLesson.title}
                </h2>

            {/* Interactive Study - Mobile Only (persistent block at top) */}
            {externalResources.length > 0 && (
              <div className="mb-6 lg:hidden">
                <h4 className="text-sm font-semibold mb-2 text-center" style={{ color: colors.heading }}>
                  Interactive Study
                </h4>
                <div className="space-y-2">
                  {externalResources.map((resource) => {
                    const blockColor = resource.block_color || '#3B82F6';
                    return (
                      <button
                        key={resource.id}
                        onClick={() => openExternalLink(resource.url)}
                        className="w-full p-3 rounded-xl hover:shadow-lg transition-all duration-200 group text-left border-2"
                        style={{
                          background: isDarkMode ? `linear-gradient(to right, ${blockColor}20, ${blockColor}30)` : `linear-gradient(to right, ${blockColor}10, ${blockColor}20)`,
                          borderColor: `${blockColor}40`,
                        }}
                      >
                        <div className="flex items-center gap-3">
                          {resource.thumbnail_url ? (
                            <img 
                              src={resource.thumbnail_url.startsWith('http') ? resource.thumbnail_url : `${BACKEND_URL}${resource.thumbnail_url}`}
                              alt={resource.title}
                              className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                            />
                          ) : (
                            <div 
                              className="w-10 h-10 rounded-xl flex items-center justify-center transition-colors"
                              style={{ backgroundColor: `${blockColor}20` }}
                            >
                              <ExternalLink className="w-5 h-5" style={{ color: blockColor }} />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium group-hover:opacity-80 transition-colors truncate" style={{ color: colors.text }}>
                              {resource.title}
                            </p>
                            <p className="text-xs truncate" style={{ color: colors.subtext }}>
                              {new URL(resource.url).hostname}
                            </p>
                          </div>
                          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-all flex-shrink-0" style={{ color: blockColor }} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

              <div className="rounded-lg shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8" style={{ backgroundColor: colors.cardBg }}>
                {/* Lesson Image/Slide */}
                {selectedLesson.image_url && (
                  <div className="mb-8">
                    <img 
                      src={`${BACKEND_URL}${selectedLesson.image_url}`}
                      alt={selectedLesson.title}
                      className="w-full rounded-lg shadow-md"
                      style={{ maxHeight: '600px', objectFit: 'contain', background: isDarkMode ? '#1e3a5f' : '#f3f4f6' }}
                    />
                  </div>
                )}

                {/* Video Player */}
                {selectedLesson.video_url && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <PlayCircle className="w-5 h-5" style={{ color: colors.accent }} />
                      <h3 className="text-lg font-semibold" style={{ color: colors.text }}>Video Content</h3>
                    </div>
                    <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
                      {/* Check if it's an uploaded video (starts with /api/files/) */}
                      {selectedLesson.video_url.startsWith('/api/files/') || selectedLesson.video_url.startsWith('/files/') ? (
                        <video
                          ref={videoPlayerRef}
                          src={selectedLesson.video_url.startsWith('/api/') 
                            ? `${BACKEND_URL}${selectedLesson.video_url}` 
                            : `${BACKEND_URL}/api${selectedLesson.video_url}`}
                          width="100%"
                          height="100%"
                          controls
                          className="w-full h-full object-contain"
                          onLoadedMetadata={() => handleVideoReady()}
                          onTimeUpdate={(e) => {
                            const video = e.target;
                            if (video.duration > 0) {
                              handleVideoProgress({ played: video.currentTime / video.duration });
                            }
                          }}
                          onError={(e) => {
                            console.error('Video player error:', e);
                            toast.error('Error loading video');
                          }}
                        />
                      ) : (
                        <ReactPlayer
                          ref={videoPlayerRef}
                          url={normalizeVimeoUrl(selectedLesson.video_url)}
                          width="100%"
                          height="100%"
                          controls={true}
                          playing={false}
                          light={false}
                          onReady={() => {
                            handleVideoReady();
                          }}
                          onProgress={handleVideoProgress}
                          onError={(e) => {
                            console.error('Video player error:', e);
                            toast.error('Error loading video');
                          }}
                          progressInterval={10000}
                          config={{
                            vimeo: {
                              playerOptions: {
                                responsive: true,
                                byline: false,
                                portrait: false,
                                title: false
                              }
                            },
                            youtube: {
                              playerVars: {
                                showinfo: 1,
                                rel: 0
                              }
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {/* Audio Player */}
                {selectedLesson.audio_url && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Volume2 className="w-5 h-5" style={{ color: colors.accent }} />
                      <h3 className="text-lg font-semibold" style={{ color: colors.text }}>Audio Content</h3>
                    </div>
                    <div className="rounded-lg p-6" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#f8fafc' }}>
                      <ReactPlayer
                        url={selectedLesson.audio_url}
                        width="100%"
                        height="60px"
                        controls
                      />
                    </div>
                  </div>
                )}

                {/* PDF Viewer - for PDF source types */}
                {selectedLesson.source_type === 'pdf' && selectedLesson.file_path && (
                  <LessonPdfViewer
                    fileUrl={`${API}/courses/${courseId}/source-file/${selectedLesson.id}`}
                    filename={`${selectedLesson.title || 'Course_Handout'}.pdf`}
                    colors={colors}
                  />
                )}

                {/* Lesson Content / Transcript - hide for PDF sources that have file_path */}
                {selectedLesson.content && !(selectedLesson.source_type === 'pdf' && selectedLesson.file_path) && (
                  <div 
                    className={`prose prose-lg max-w-none ${isDarkMode ? 'prose-invert' : ''}`}
                    style={{ color: colors.text }}
                    onClick={(e) => {
                      if (e.target.tagName === 'A' && e.target.href) {
                        e.preventDefault();
                        openExternalLink(e.target.href);
                      }
                    }}
                  >
                    {selectedLesson.content.includes('<') ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedLesson.content }} />
                    ) : (
                      <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({node, ...props}) => (
                            <a
                              {...props}
                              onClick={(e) => {
                                e.preventDefault();
                                openExternalLink(props.href);
                              }}
                              className="text-blue-500 hover:text-blue-400 underline cursor-pointer"
                            />
                          )
                        }}
                      >
                        {selectedLesson.content}
                      </ReactMarkdown>
                    )}
                  </div>
                )}

                {/* Mark Complete Button */}
                {!completedLessons.has(selectedLesson.id) && (
                  <Button
                    onClick={() => markLessonComplete(selectedLesson.id)}
                    className="w-full"
                    style={{ background: colors.accent }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Complete
                  </Button>
                )}

                {completedLessons.has(selectedLesson.id) && (
                  <div className="flex items-center justify-center gap-2 text-green-500 py-3">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Completed</span>
                  </div>
                )}

                {/* Navigation Footer */}
                <div className="flex justify-between items-center mt-6 pt-6" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <Button
                    variant="outline"
                    onClick={goToPreviousLesson}
                    disabled={currentLessonIndex === 0}
                    style={{ borderColor: colors.border, color: colors.text }}
                  >
                    <PrevIcon className="w-4 h-4 mr-2" />
                    Previous
                  </Button>
                  
                  <span className="text-sm" style={{ color: colors.subtext }}>
                    Lesson {currentLessonIndex + 1} of {allLessons.length}
                  </span>

                  <Button
                    onClick={goToNextLesson}
                    disabled={currentLessonIndex >= allLessons.length - 1}
                    style={{ background: colors.accent }}
                  >
                    Next
                    <NextIcon className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
              </>
            ) : (
              /* No lessons available - Empty state */
              <div className="flex flex-col items-center justify-center h-full py-16">
                <FileText className="w-20 h-20 mb-6" style={{ color: colors.subtext }} />
                <h2 className="text-2xl font-bold mb-3" style={{ color: colors.heading }}>
                  No Lessons Available Yet
                </h2>
                <p className="text-center max-w-md" style={{ color: colors.subtext }}>
                  This course is being prepared. Lessons will appear here once the instructor adds them.
                </p>
              </div>
            )}
          </div>

          {/* RIGHT SIDEBAR: Instructor Bio (Hidden on Mobile) */}
          <div className="w-80 p-6 flex-shrink-0 hidden lg:block">
            {/* Instructor Bio */}
            <Card className="p-4 rounded-lg" style={{ backgroundColor: colors.cardBg }}>
              <div className="text-center mb-3">
                {course.instructor_photo_url ? (
                  <img
                    src={`${API}${course.instructor_photo_url}`}
                    alt={course.instructor_name || "Instructor"}
                    className="w-20 h-20 rounded-full mx-auto mb-3 object-cover border-2"
                    style={{ borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center border-2" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#e5e7eb', borderColor: isDarkMode ? '#3b82f6' : '#d1d5db' }}>
                    <User className="w-10 h-10" style={{ color: colors.subtext }} />
                  </div>
                )}
                <h3 className="text-base font-bold mb-1" style={{ color: colors.heading }}>
                  {course.instructor_name || "Course Instructor"}
                </h3>
                <p className="text-sm mb-3" style={{ color: colors.subtext }}>Instructor</p>
              </div>
              
              {course.instructor_bio && (
                <div className="text-sm leading-relaxed mb-4" style={{ color: colors.text }}>
                  <p>{course.instructor_bio}</p>
                </div>
              )}

              {!course.instructor_bio && (
                <p className="text-sm italic text-center mb-4" style={{ color: colors.subtext }}>
                  No bio available
                </p>
              )}

              {/* Interactive Study - Persistent block below Instructor Bio */}
              {externalResources.length > 0 && (
                <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <h4 className="text-sm font-semibold mb-3 text-center" style={{ color: colors.heading }}>
                    Interactive Study
                  </h4>
                  <div className="space-y-2">
                    {externalResources.map((resource) => {
                      const blockColor = resource.block_color || '#3B82F6';
                      const hasCustomColor = resource.block_color && resource.block_color !== '#3B82F6';
                      return (
                        <button
                          key={resource.id}
                          onClick={() => openExternalLink(resource.url)}
                          className="w-full p-3 rounded-lg hover:shadow-md transition-all duration-200 group text-left border-2"
                          style={{
                            background: isDarkMode ? `linear-gradient(to right, ${blockColor}20, ${blockColor}30)` : `linear-gradient(to right, ${blockColor}10, ${blockColor}20)`,
                            borderColor: `${blockColor}40`,
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = `${blockColor}80`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = `${blockColor}40`;
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {resource.thumbnail_url ? (
                              <img 
                                src={resource.thumbnail_url.startsWith('http') ? resource.thumbnail_url : `${BACKEND_URL}${resource.thumbnail_url}`}
                                alt={resource.title}
                                className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              />
                            ) : (
                              <div 
                                className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors flex-shrink-0"
                                style={{ backgroundColor: `${blockColor}20` }}
                              >
                                <ExternalLink className="w-4 h-4" style={{ color: blockColor }} />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium group-hover:opacity-80 transition-colors truncate" style={{ color: colors.text }}>
                                {resource.title || 'Open Resource'}
                              </p>
                              <p className="text-xs truncate" style={{ color: colors.subtext }}>
                                {new URL(resource.url).hostname}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Certificate View */}
              {progress?.assessment_passed && (
                <div className="mt-4 pt-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <Button
                    onClick={openCertificate}
                    className="w-full"
                    size="sm"
                    style={{ background: colors.accent }}
                  >
                    <Award className="w-4 h-4 mr-2" />
                    View Certificate
                  </Button>
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Assessment Modal for Lesson View */}
        {showAssessment && assessmentData && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={() => setShowAssessment(false)}
          >
            <div 
              className="rounded-lg shadow-2xl w-[95vw] sm:w-[90vw] md:w-[800px] max-h-[90vh] overflow-y-auto"
              style={{ backgroundColor: colors.modalBg }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 px-6 py-4 flex justify-between items-center rounded-t-lg z-10" style={{ backgroundColor: colors.modalBg, borderBottom: `1px solid ${colors.border}` }}>
                <h2 className="text-xl font-bold" style={{ color: colors.heading }}>
                  Course Assessment
                </h2>
                <button
                  onClick={() => setShowAssessment(false)}
                  className={`transition-colors rounded-full w-8 h-8 flex items-center justify-center ${isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'hover:bg-gray-100'}`}
                >
                  <X className="w-5 h-5" style={{ color: isDarkMode ? '#ffffff' : '#9ca3af' }} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Info Banner */}
                <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#eff6ff', borderColor: isDarkMode ? '#3b82f6' : '#bfdbfe' }}>
                  <p className="text-sm" style={{ color: isDarkMode ? '#93c5fd' : '#1e3a8a' }}>
                    <strong>Note:</strong> You need {course.passing_grade}% to pass this assessment. 
                    This assessment has {assessmentData.questions?.length || 0} questions.
                  </p>
                </div>

                {/* Questions */}
                <div className="space-y-6">
                  {assessmentData.questions?.map((q, index) => (
                    <div key={index} className="rounded-lg p-4 border" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#f9fafb', borderColor: colors.border }}>
                      <h3 className="font-semibold mb-4 text-base" style={{ color: colors.text }}>
                        Question {index + 1}: {q.question}
                      </h3>
                      <div className="space-y-2">
                        {q.options.map((option, optIndex) => (
                          <label
                            key={optIndex}
                            className="flex items-start gap-3 p-3 rounded-md cursor-pointer border transition-all"
                            style={{ 
                              borderColor: assessmentAnswers[index] === option ? colors.accent : 'transparent',
                              backgroundColor: assessmentAnswers[index] === option ? (isDarkMode ? '#0f2744' : '#ffffff') : 'transparent'
                            }}
                          >
                            <input
                              type="radio"
                              name={`question-${index}`}
                              value={option}
                              checked={assessmentAnswers[index] === option}
                              onChange={() => setAssessmentAnswers({
                                ...assessmentAnswers,
                                [index]: option
                              })}
                              className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600"
                            />
                            <span className="text-sm" style={{ color: colors.text }}>{option}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 mt-6 pt-6" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <Button
                    variant="outline"
                    onClick={() => setShowAssessment(false)}
                    className="px-6"
                    style={{ borderColor: colors.border, color: colors.text }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitAssessment}
                    disabled={submittingAssessment || Object.keys(assessmentAnswers).length < (assessmentData.questions?.length || 0)}
                    style={{ background: colors.accent }}
                    className="px-6 hover:opacity-90"
                  >
                    {submittingAssessment ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Submitting...
                      </span>
                    ) : (
                      "Submit Assessment"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Modal for Lesson View */}
        <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
          <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden" style={{ backgroundColor: colors.modalBg }}>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.heading }}>
                <Award className="w-6 h-6 text-yellow-500" />
                Congratulations! Your Certificate of Completion
              </DialogTitle>
            </DialogHeader>

            <div className="mt-4">
              {/* Certificate Preview */}
              {certificateUrl && (
                <div className="rounded-lg overflow-hidden" style={{ height: '70vh', backgroundColor: isDarkMode ? '#1e3a5f' : '#f3f4f6' }}>
                  <iframe
                    src={certificateUrl}
                    title="Certificate of Completion"
                    className="w-full h-full"
                    style={{ border: 'none' }}
                  />
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 mt-6">
                <Button
                  onClick={downloadCertificate}
                  className="flex-1"
                  style={{ background: colors.accent }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Certificate
                </Button>
                
                <Button
                  onClick={() => {
                    if (certificateUrl) {
                      window.open(certificateUrl, '_blank');
                    }
                  }}
                  variant="outline"
                  className="flex-1"
                  style={{ borderColor: colors.border, color: colors.text }}
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Certificate
                </Button>
                
                <Button
                  onClick={() => setShowCertificate(false)}
                  variant="outline"
                  style={{ borderColor: colors.border, color: colors.text }}
                >
                  Close
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: colors.pageBg }}>
      {/* Header */}
      <div className="shadow-md sticky top-0 z-30" style={{ backgroundColor: colors.headerBg }}>
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-full transition-colors flex-shrink-0"
                style={{ color: colors.heading }}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img src={LOGO_URL} alt="Logo" className="h-6 sm:h-8 flex-shrink-0 cursor-pointer" onClick={() => navigate("/")} style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
              <h1 className="text-base sm:text-xl font-bold truncate" style={{ color: colors.heading }}>
                {course.title}
              </h1>
            </div>
            <DarkModeToggle />
          </div>

          {/* Progress Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl">
            {/* Progress Card */}
            <Card className="p-3" style={{ backgroundColor: colors.cardBg }}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" style={{ color: colors.accent }} />
                  <span className="text-sm font-medium" style={{ color: colors.text }}>Course Progress</span>
                </div>
                <span className="text-lg font-bold" style={{ color: completionPercentage > 50 ? '#059669' : '#10b981' }}>
                  {Math.round(completionPercentage)}%
                </span>
              </div>
              <div className="relative h-2 rounded-full overflow-hidden" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}>
                <div 
                  className="absolute top-0 left-0 h-full transition-all duration-500 rounded-full"
                  style={{ 
                    width: `${completionPercentage}%`,
                    background: `linear-gradient(to right, #86efac, ${completionPercentage > 50 ? '#059669' : '#10b981'})`
                  }}
                />
              </div>
              <p className="text-xs mt-1" style={{ color: colors.subtext }}>
                {completedLessons.size} / {allLessons.length} lessons completed
              </p>
            </Card>

            {/* Assessment Card */}
            <Card className={`p-3 transition-colors`} style={{ backgroundColor: isDarkMode ? (progress?.assessment_passed ? '#1e3a8a' : '#14532d') : (progress?.assessment_passed ? '#eff6ff' : '#f0fdf4'), borderColor: isDarkMode ? (progress?.assessment_passed ? '#3b82f6' : '#22c55e') : (progress?.assessment_passed ? '#93c5fd' : '#86efac') }}>
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className={`w-4 h-4 ${progress?.assessment_passed ? 'text-blue-500' : 'text-green-500'}`} />
                <span className="text-sm font-medium" style={{ color: colors.text }}>Assessment</span>
              </div>
              {progress?.assessment_passed ? (
                <div className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-500" />
                  <span className="text-sm font-medium text-blue-500">Passed!</span>
                </div>
              ) : (
                <Button
                  size="sm"
                  onClick={loadAssessment}
                  disabled={loadingAssessment}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {loadingAssessment ? "Loading..." : "Take Assessment"}
                </Button>
              )}
            </Card>
          </div>
        </div>
      </div>

      {/* Main Content - 3 Column Layout */}
      <div className="max-w-screen-2xl mx-auto px-6 py-8">
        <div className="grid grid-cols-12 gap-6">
          {/* LEFT: Instructor Bio (3 columns = 25%) */}
          <div className="col-span-3">
            <Card className="p-6 sticky top-32" style={{ backgroundColor: colors.cardBg }}>
              <div className="text-center mb-4">
                {course.instructor_photo_url ? (
                  <img
                    src={`${API}${course.instructor_photo_url}`}
                    alt={course.instructor_name || "Instructor"}
                    className="w-32 h-32 rounded-full mx-auto mb-4 object-cover border-4"
                    style={{ borderColor: isDarkMode ? '#1e3a5f' : '#e5e7eb' }}
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full mx-auto mb-4 flex items-center justify-center border-4" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#e5e7eb', borderColor: isDarkMode ? '#3b82f6' : '#d1d5db' }}>
                    <User className="w-16 h-16" style={{ color: colors.subtext }} />
                  </div>
                )}
                <h3 className="text-lg font-bold mb-1" style={{ color: colors.heading }}>
                  {course.instructor_name || "Course Instructor"}
                </h3>
                <p className="text-sm mb-4" style={{ color: colors.subtext }}>Instructor</p>
              </div>
              
              {course.instructor_bio && (
                <div className="text-sm leading-relaxed" style={{ color: colors.text }}>
                  <p>{course.instructor_bio}</p>
                </div>
              )}

              {!course.instructor_bio && (
                <p className="text-sm italic text-center" style={{ color: colors.subtext }}>
                  No bio available
                </p>
              )}

              {/* Certificate View */}
              {progress?.assessment_passed && (
                <div className="mt-6 pt-6" style={{ borderTop: `1px solid ${colors.border}` }}>
                  <Button
                    onClick={openCertificate}
                    className="w-full"
                    style={{ background: colors.accent }}
                  >
                    <Award className="w-4 h-4 mr-2" />
                    View Certificate
                  </Button>
                </div>
              )}
            </Card>
          </div>

          {/* CENTER: Lessons List (6 columns = 50%) */}
          <div className="col-span-6">
            <Card className="p-6" style={{ backgroundColor: colors.cardBg }}>
              <h2 className="text-2xl font-bold mb-6" style={{ color: colors.heading }}>
                Course Lessons
              </h2>

              {allLessons.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: colors.subtext }} />
                  <p style={{ color: colors.subtext }}>No lessons available yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {allLessons.map((lesson, index) => {
                    const isCompleted = completedLessons.has(lesson.id);
                    return (
                      <Card
                        key={lesson.id}
                        className={`p-4 cursor-pointer transition-all hover:shadow-lg`}
                        style={{ 
                          backgroundColor: isCompleted ? (isDarkMode ? '#14532d' : '#f0fdf4') : colors.cardBg,
                          borderColor: isCompleted ? (isDarkMode ? '#22c55e' : '#bbf7d0') : colors.border
                        }}
                        onClick={() => openLesson(lesson, index)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 flex-1">
                            <div className="flex-shrink-0">
                              {isCompleted ? (
                                <CheckCircle className="w-6 h-6 text-green-500" />
                              ) : (
                                <div className="w-6 h-6 rounded-full border-2" style={{ borderColor: colors.subtext }} />
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-base" style={{ color: colors.text }}>
                                Lesson {index + 1}: {lesson.title}
                              </h3>
                              {lesson.video_url && (
                                <div className="flex items-center gap-1 mt-1">
                                  <PlayCircle className="w-3 h-3" style={{ color: colors.subtext }} />
                                  <span className="text-xs" style={{ color: colors.subtext }}>Video</span>
                                </div>
                              )}
                              {lesson.audio_url && (
                                <div className="flex items-center gap-1 mt-1">
                                  <Volume2 className="w-3 h-3" style={{ color: colors.subtext }} />
                                  <span className="text-xs" style={{ color: colors.subtext }}>Audio</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5" style={{ color: colors.subtext }} />
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          {/* RIGHT: AI Assistant (3 columns = 25%) */}
          <div className="col-span-3">
            <Card className="sticky top-32 flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)', backgroundColor: colors.cardBg }}>
              {/* Header */}
              <div className="px-4 py-4" style={{ borderBottom: `1px solid ${colors.border}` }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: colors.accent }}>
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-base" style={{ color: colors.heading }}>
                      AI Surgical Assistant
                    </h3>
                    <p className="text-xs" style={{ color: colors.subtext }}>Ask me anything</p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <div 
                ref={chatMessagesRef}
                className="flex-1 overflow-y-auto p-4 space-y-3"
                style={{ maxHeight: '400px' }}
              >
                {chatMessages.length === 0 ? (
                  <div className="text-center py-8">
                    <Brain className="w-12 h-12 mx-auto mb-3" style={{ color: colors.subtext }} />
                    <p className="text-sm" style={{ color: colors.subtext }}>
                      Start a conversation about the course content
                    </p>
                  </div>
                ) : (
                  chatMessages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[85%] p-3 rounded-lg text-sm ${
                          msg.role === 'user'
                            ? 'bg-blue-600 text-white'
                            : ''
                        }`}
                        style={msg.role !== 'user' ? { backgroundColor: isDarkMode ? '#1e3a5f' : '#f3f4f6', color: colors.text } : {}}
                      >
                        {msg.content}
                      </div>
                    </div>
                  ))
                )}
                {chatLoading && (
                  <div className="flex justify-start">
                    <div className="p-3 rounded-lg" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#f3f4f6' }}>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: colors.subtext }} />
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ animationDelay: '0.1s', backgroundColor: colors.subtext }} />
                          <div className="w-2 h-2 rounded-full animate-bounce" style={{ animationDelay: '0.2s', backgroundColor: colors.subtext }} />
                        </div>
                        <span className="text-xs italic" style={{ color: colors.subtext }}>
                          <LoadingQuip />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="p-4" style={{ borderTop: `1px solid ${colors.border}` }}>
                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                    placeholder="Ask a question..."
                    className="flex-1"
                    disabled={chatLoading}
                    style={{ backgroundColor: colors.inputBg, color: colors.inputText, borderColor: colors.border }}
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={chatLoading || !chatInput.trim()}
                    size="icon"
                    style={{ background: colors.accent }}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Old lesson modal code removed - now using full-page view */}

      {/* Survey Dialog */}
      <Dialog open={showSurvey} onOpenChange={setShowSurvey}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[95vw] sm:w-full" style={{ backgroundColor: colors.modalBg }}>
          <DialogHeader>
            <DialogTitle className="text-lg sm:text-2xl font-bold" style={{ color: colors.heading }}>
              Course Survey (Required for CE Credit)
            </DialogTitle>
          </DialogHeader>

          {survey && (
            <div className="space-y-6 mt-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: isDarkMode ? '#1e3a5f' : '#eff6ff' }}>
                <p className="text-sm" style={{ color: isDarkMode ? '#93c5fd' : '#1e3a8a' }}>
                  Please complete this brief survey to receive your continuing education certificate.
                </p>
              </div>

              {survey.questions.map((q, idx) => {
                const isMultipleChoice = q.type === "multiple_choice";
                
                return (
                  <Card key={idx} className="p-4" style={{ backgroundColor: colors.cardBg }}>
                    <h3 className="font-semibold mb-3" style={{ color: colors.text }}>
                      {idx + 1}. {q.question}
                    </h3>
                    
                    {isMultipleChoice ? (
                      <div className="space-y-2">
                        {q.options.map((option, optIdx) => (
                          <label
                            key={optIdx}
                            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer border"
                            style={{ borderColor: colors.border, backgroundColor: surveyAnswers[idx] === option ? (isDarkMode ? '#1e3a5f' : '#f3f4f6') : 'transparent' }}
                          >
                            <input
                              type="radio"
                              name={`survey-q-${idx}`}
                              value={option}
                              checked={surveyAnswers[idx] === option}
                              onChange={(e) => {
                                const updated = [...surveyAnswers];
                                updated[idx] = e.target.value;
                                setSurveyAnswers(updated);
                              }}
                              className="w-4 h-4"
                            />
                            <span style={{ color: colors.text }}>{option}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <Textarea
                        value={surveyAnswers[idx] || ""}
                        onChange={(e) => {
                          const updated = [...surveyAnswers];
                          updated[idx] = e.target.value;
                          setSurveyAnswers(updated);
                        }}
                        rows={4}
                        placeholder="Your answer..."
                        style={{ backgroundColor: colors.inputBg, color: colors.inputText, borderColor: colors.border }}
                      />
                    )}
                  </Card>
                );
              })}

              <Button
                onClick={submitSurvey}
                disabled={submittingSurvey || surveyAnswers.some(a => !a)}
                className="w-full"
                style={{ background: colors.accent }}
              >
                {submittingSurvey ? "Submitting..." : "Submit Survey & Get Certificate"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Certificate Modal */}
      <Dialog open={showCertificate} onOpenChange={setShowCertificate}>
        <DialogContent className="max-w-4xl max-h-[95vh] overflow-hidden" style={{ backgroundColor: colors.modalBg }}>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ color: colors.heading }}>
              <Award className="w-6 h-6 text-yellow-500" />
              Congratulations! Your Certificate of Completion
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            {/* Certificate Preview */}
            {certificateUrl && (
              <div className="rounded-lg overflow-hidden" style={{ height: '70vh', backgroundColor: isDarkMode ? '#1e3a5f' : '#f3f4f6' }}>
                <iframe
                  src={certificateUrl}
                  title="Certificate of Completion"
                  className="w-full h-full"
                  style={{ border: 'none' }}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-4 mt-6">
              <Button
                onClick={downloadCertificate}
                className="flex-1"
                style={{ background: '#102862' }}
              >
                <Download className="w-4 h-4 mr-2" />
                Download Certificate
              </Button>
              
              <Button
                onClick={() => {
                  if (certificateUrl) {
                    window.open(certificateUrl, '_blank');
                  }
                }}
                variant="outline"
                className="flex-1"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Certificate
              </Button>
              
              <Button
                onClick={() => setShowCertificate(false)}
                variant="outline"
              >
                Close
              </Button>
            </div>

            {/* Celebration Message */}
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-900 text-center">
                🎉 <strong>Course Complete!</strong> You have successfully earned {course.ce_hours} CE hours. 
                Your certificate has been generated and is ready to download.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* External Link Modal */}
      <Dialog open={showExternalLinkModal} onOpenChange={setShowExternalLinkModal}>
        <DialogContent className="max-w-6xl max-h-[95vh] p-0 overflow-hidden">
          <div className="relative w-full h-[90vh]">
            {/* Large Close Button */}
            <button
              onClick={() => setShowExternalLinkModal(false)}
              className="absolute top-4 right-4 z-50 p-3 bg-white rounded-full shadow-lg hover:bg-gray-100 transition-colors"
              aria-label="Close external link"
            >
              <X className="w-8 h-8 text-gray-700 hover:text-gray-900" />
            </button>
            
            {/* External Content */}
            {externalLinkUrl && (
              <iframe
                src={externalLinkUrl}
                title="External Content"
                className="w-full h-full border-none"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assessment Modal - Simple Overlay */}
      {showAssessment && assessmentData && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
          onClick={() => setShowAssessment(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-2xl w-[95vw] sm:w-[90vw] md:w-[800px] max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-lg z-10">
              <h2 className="text-xl font-bold" style={{ color: '#102862' }}>
                Course Assessment
              </h2>
              <button
                onClick={() => setShowAssessment(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> You need {course.passing_grade}% to pass this assessment. 
                  This assessment has {assessmentData.questions?.length || 0} questions.
                </p>
              </div>

              {/* Questions */}
              <div className="space-y-6">
                {assessmentData.questions?.map((q, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h3 className="font-semibold mb-4 text-base">
                      Question {index + 1}: {q.question}
                    </h3>
                    <div className="space-y-2">
                      {q.options.map((option, optIndex) => (
                        <label
                          key={optIndex}
                          className="flex items-start gap-3 p-3 rounded-md hover:bg-white cursor-pointer border border-transparent hover:border-blue-300 transition-all"
                        >
                          <input
                            type="radio"
                            name={`question-${index}`}
                            value={option}
                            checked={assessmentAnswers[index] === option}
                            onChange={() => setAssessmentAnswers({
                              ...assessmentAnswers,
                              [index]: option
                            })}
                            className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600"
                          />
                          <span className="text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowAssessment(false)}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button
                  onClick={submitAssessment}
                  disabled={submittingAssessment || Object.keys(assessmentAnswers).length < (assessmentData.questions?.length || 0)}
                  style={{ background: '#102862' }}
                  className="px-6 hover:opacity-90"
                >
                  {submittingAssessment ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Submitting...
                    </span>
                  ) : (
                    "Submit Assessment"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CoursePage;
